// mono  at 16 feb 21.32

import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import '../../../Styles/Colorize/colorize.scss';
import '../../../Styles/Colorize/hslInput.scss';
import '../../../Styles/Colorize/tools.scss';
import '../../../Styles/Colorize/editVariable.scss';
import '../../../Styles/Colorize/editScale.scss';
import '../../../Styles/Colorize/squares.scss';
import CustomColorPicker from "../CustomColorPicker";
import debounce from "lodash.debounce";
import { generatePaletteFromColor as importedGeneratePalette } from "../ColorSchemeGenerator";
import { getContrastRatio } from "../getTextColor";
import chroma from "chroma-js";
import {
  hexToHSL,
  hslToHex,
  validateHex,
  hexToRgb,
  colorDistance,
  getExactHslString
} from "../ColorUtils";
import { useMembershipGuard } from '../../Auth/useMembershipGuard';
import { useAuth } from '../../../contexts/AuthContext';


const Mono = forwardRef(({ color, setColor, palette, setPalette, selectedIndex, setSelectedIndex, editIndex, setEditIndex, paletteModifiers, setPaletteModifiers, squareNames, setBaseColorName, onSave, selectionMode, selectionName, onOpenExport, onOpenExportToFigma, onOpenContrastGrid, onColorChange, onModifiersChange, isUpdatingFromURL, onTailwindFamilyChange }, ref) => {
  const { executeMembershipGuard } = useMembershipGuard();
  const { subscriptionActive } = useAuth();
  const [currentColor, setCurrentColor] = useState(color || '');
  const [inputValue, setInputValue] = useState(color || '');
  const [validColor, setValidColor] = useState(color || '');
  const [colorFormat, setColorFormat] = useState("hsl");
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [lightness, setLightness] = useState(0);
  const [validHex, setValidHex] = useState(true);
  const [isRendered, setIsRendered] = useState(false);
  const isRendering = useRef(true);
  const isThrottled = useRef(false);

  const editRef = useRef(null);
  const isLoadingFromURL = useRef(false);
  const hasAppliedTailwindFromURL = useRef(false);
  const [colorName, setColorName] = useState("");

  const [hueHover, setHueHover] = useState(false);
  const [satLeftHover, setSatLeftHover] = useState(false);
  const [satRightHover, setSatRightHover] = useState(false);
  const [lightLeftHover, setLightLeftHover] = useState(false);
  const [lightRightHover, setLightRightHover] = useState(false);

  const [enhancedColorDatabase, setEnhancedColorDatabase] = useState({});


  // show either tools or editor
  // const [showTools, setShowTools] = useState(true);
  const [showEditor, setShowEditor] = useState(false);

  // show either editScale or editVarialble
  const [showEditScale, setShowEditScale] = useState(false);
  const [showEditVariable, setShowEditVariable] = useState(true);

  // use this to prevent glitched in indiv edit mode
  const [skipPaletteRegeneration, setSkipPaletteRegeneration] = useState(false);

  // edit scale mode
  const [editScaleMode, setEditScaleMode] = useState('hue'); // Default to hue mode

  const isHueSliderMoving = useRef(false);
  const originalHuesSnapshot = useRef(null);

  const [isBaseHueSliderMoving, setIsBaseHueSliderMoving] = useState(false);
  const [isBaseSaturationSliderMoving, setIsBaseSaturationSliderMoving] = useState(false);
  const [isBaseLightnessSliderMoving, setIsBaseLightnessSliderMoving] = useState(false);

  // Add these state variables to store the original (precise) HSL values
  // is to make sure your hsl values of the base color dont jump when using the switch button
  const [preciseHue, setPreciseHue] = useState(0);
  const [preciseSaturation, setPreciseSaturation] = useState(0);
  const [preciseLightness, setPreciseLightness] = useState(0);

  // tailwind color families
  const [tailwindColors, setTailwindColors] = useState({});
  const [showTailwindDropdown, setShowTailwindDropdown] = useState(false);
  const [selectedTailwindFamily, setSelectedTailwindFamily] = useState('');
  const [tailwindVersion, setTailwindVersion] = useState('v3');

  useImperativeHandle(ref, () => ({
    getBaseGeneratedPalette: () => baseGeneratedPalette,
    getTailwindFamily: () => selectedTailwindFamily,
    loadSavedPalette: (savedData) => {

      // Lock updates to prevent automatic regeneration
      isUpdatingRef.current = true;

      // Skip palette generation entirely - just use the saved values
      setSkipPaletteRegeneration(true);

      // Set all state directly from saved data
      setBaseGeneratedPalette(savedData.baseGeneratedPalette || savedData.palette);
      setPalette(savedData.palette);
      setColor(savedData.color);
      setCurrentColor(savedData.color);
      setValidColor(savedData.color);
      setInputValue(savedData.color);
      setPaletteModifiers(savedData.paletteModifiers || {
        scale: {
          hue: 0,
          lightnessLeft: 0,
          lightnessRight: 0,
          saturationLeft: 0,
          saturationRight: 0
        },
        individual: {}
      });

      if (savedData.selectedIndex !== undefined) {
        setSelectedIndex(savedData.selectedIndex);
      }

      // Update HSL values if needed
      if (savedData.color) {
        const { h, s, l } = hexToHSL(savedData.color);
        setPreciseHue(h);
        setPreciseSaturation(s);
        setPreciseLightness(l);
        setHue(h);
        setSaturation(s);
        setLightness(l);
      }

      // Keep skip flag active for a moment
      setTimeout(() => {
        isUpdatingRef.current = false;

        // Only disable skip after a longer delay to make sure
        // no regeneration happens after loading
        setTimeout(() => {
          setSkipPaletteRegeneration(false);
        }, 500);
      }, 100);

    },
    colorizeMe: (newColor) => {
      console.log('colorize me')
      // Use the same code from the existing "Colorize me" button handler
      resetVariableEditsOnNewColorInput();
      const colorToUse = newColor || generateRandomColor();
      setInputValue(colorToUse);
      setValidColor(colorToUse);
      setColor(colorToUse);
      setValidHex(true);
      const { h, s, l } = hexToHSL(colorToUse);
      setHue(h);
      setSaturation(s);
      setLightness(l);
      setPreciseHue(h);
      setPreciseSaturation(s);
      setPreciseLightness(l);
      if (typeof window !== 'undefined') {
        window._colorFamilyTracker = null;
      }
      setSelectedTailwindFamily('');
      generatePaletteFromColor(colorToUse);

      if (!isUpdatingFromURL && onColorChange) {
        onColorChange(colorToUse);
      }
    },

    loadFromURLState: (urlState) => {
      // FORCE isUpdatingRef to false and set loading flag
      isUpdatingRef.current = false;  // ADD THIS LINE
      isLoadingFromURL.current = true;
      hasAppliedTailwindFromURL.current = false;

      // Set Tailwind family if it exists
      if (urlState.tailwindFamily) {
        setSelectedTailwindFamily(urlState.tailwindFamily);
      }

      // Load color
      if (urlState.color) {
        setInputValue(urlState.color);
        setValidColor(urlState.color);
        setColor(urlState.color);
        setCurrentColor(urlState.color);
        setValidHex(true);

        const { h, s, l } = hexToHSL(urlState.color);
        setHue(h);
        setSaturation(s);
        setLightness(l);
        setPreciseHue(h);
        setPreciseSaturation(s);
        setPreciseLightness(l);
      }

      // Load modifiers BEFORE generating palette
      if (urlState.paletteModifiers) {
        setPaletteModifiers(urlState.paletteModifiers);
      }

      // Use requestAnimationFrame to ensure all state updates are processed

      requestAnimationFrame(() => {
        if (!urlState.tailwindFamily && urlState.color) {
          requestAnimationFrame(() => {
            generatePaletteFromColor(urlState.color);
            isLoadingFromURL.current = false;
          });
        }

        // isLoadingFromURL.current = false;
      });
    }


  }));





















  // to make sure your hex displayed can be 3 characters only
  const [displayHex, setDisplayHex] = useState(color);

  const shadeOrder = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"];

  const [baseGeneratedPalette, setBaseGeneratedPalette] = useState([]);

  const generateBasePalette = (baseColor) => {
    try {
      const { palette: paletteObject, mappedShade } = importedGeneratePalette(baseColor);

      // Determine the index of the selected shade
      const baseIndex = shadeOrder.indexOf(mappedShade);

      // Convert all colors to HSL format for consistency
      const basePalette = shadeOrder.map((shade, index) => {
        // If this is the base color's index, use the original baseColor directly
        if (index === baseIndex) {
          return baseColor;
        }

        const color = paletteObject[shade];
        if (color.startsWith('hsl')) return color;

        // For other colors, use your HSL conversion
        const { h, s, l } = hexToHSL(color);
        return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
      });

      // Store this base palette
      setBaseGeneratedPalette(basePalette);

      return {
        basePalette,
        baseIndex: baseIndex !== -1 ? baseIndex : 5 // Default to index 5 if not found
      };
    } catch (err) {
      console.error('Error in generateBasePalette:', err);
      return { basePalette: [], baseIndex: 5 };
    }
  };

  // Function to apply ALL modifications to the base palette
  const applyAllModifications = () => {
    if (isUpdatingRef.current || baseGeneratedPalette.length === 0) {
      return;
    }

    const baseIndex = selectedIndex;

    // Apply modifications to each color in the palette
    const finalPalette = baseGeneratedPalette.map((baseColor, idx) => {
      // Base color (selected) remains unchanged
      if (idx === baseIndex) return baseColor;

      // Start with the base color's HSL values
      const { h, s, l } = getExactHslString(baseColor);

      // Determine position relative to base color
      const isLeft = idx < baseIndex;
      const isRight = idx > baseIndex;
      const distance = Math.abs(idx - baseIndex);

      // First get individual edits if they exist
      const individualEdits = paletteModifiers.individual[idx] || {};

      // Calculate scale modifications
      let scaleHueModification = 0;
      let scaleSaturationModification = 0;
      let scaleLightnessModification = 0;

      // Apply hue scale modification
      if (paletteModifiers.scale.hue !== 0) {
        const hueShift = paletteModifiers.scale.hue;
        if (isLeft) {
          scaleHueModification = hueShift * distance;
        } else if (isRight) {
          scaleHueModification = -hueShift * distance;
        }
      }

      // Apply saturation scale modification
      if (isLeft && paletteModifiers.scale.saturationLeft !== 0) {
        scaleSaturationModification = paletteModifiers.scale.saturationLeft;
      } else if (isRight && paletteModifiers.scale.saturationRight !== 0) {
        scaleSaturationModification = paletteModifiers.scale.saturationRight;
      }

      // Apply lightness scale modification
      if (isLeft && paletteModifiers.scale.lightnessLeft !== 0) {
        scaleLightnessModification = paletteModifiers.scale.lightnessLeft;
      } else if (isRight && paletteModifiers.scale.lightnessRight !== 0) {
        scaleLightnessModification = paletteModifiers.scale.lightnessRight;
      }

      // Apply all modifications
      let finalH = h;
      let finalS = s;
      let finalL = l;

      // Apply hue modifications (handling 360-degree wrapping properly)
      if (individualEdits.h !== undefined) {
        // Special case for hue = 360
        if (individualEdits.h === 360) {
          finalH = 360;
        } else {
          // Normal case
          finalH = (individualEdits.h + scaleHueModification) % 360;
          if (finalH < 0) finalH += 360;
        }
      } else {
        // Otherwise apply scale modification to the base hue
        finalH = (h + scaleHueModification) % 360;
        if (finalH < 0) finalH += 360;
      }

      // Apply saturation modifications
      if (individualEdits.s !== undefined) {
        // If individually edited, apply scale modification to the individual edit
        // (The value here could potentially be outside 0-100 range, so we clamp for safety)
        finalS = Math.max(0, Math.min(100, individualEdits.s + scaleSaturationModification));
      } else {
        // Otherwise apply scale modification to the base saturation
        finalS = Math.max(0, Math.min(100, s + scaleSaturationModification));
      }

      // Apply lightness modifications
      if (individualEdits.l !== undefined) {
        // If individually edited, apply scale modification to the individual edit
        // (The value here could potentially be outside 0-100 range, so we clamp for safety)
        finalL = Math.max(0, Math.min(100, individualEdits.l + scaleLightnessModification));
      } else {
        // Otherwise apply scale modification to the base lightness
        finalL = Math.max(0, Math.min(100, l + scaleLightnessModification));
      }

      // Return the final color with all modifications applied
      return `hsl(${Math.round(finalH)}, ${Math.round(finalS)}%, ${Math.round(finalL)}%)`;
    });
    // Update the palette state
    setPalette(finalPalette);
  };

  // Main function to generate a palette from a base color
  const generatePaletteFromColor = (baseColor) => {


    // Don't generate palette for empty color
    if (!baseColor || baseColor === '') {
      return;
    }

    if (isUpdatingRef.current) {
      return;
    }

    // First generate the base palette without any modifications
    const { baseIndex } = generateBasePalette(baseColor);


    // Update the selected index
    setSelectedIndex(baseIndex !== -1 ? baseIndex : null);

    // Then apply all modifications
    applyAllModifications();
  };

  const updateScaleModifier = (type, value) => {
    const newModifiers = {
      ...paletteModifiers,
      scale: {
        ...paletteModifiers.scale,
        [type]: value
      }
    };

    setPaletteModifiers(newModifiers);

    // ADD THIS: Notify parent component for URL update (if not loading from URL)
    if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
      onModifiersChange(newModifiers);
    }
    // Immediately apply all modifications after updating the scale
    applyAllModifications();
  };

  // Update an individual color HSL property
  const updateIndividualColor = (index, property, value) => {

    setPaletteModifiers(prev => {
      const existing = prev.individual[index] || {};

      // Calculate the scale modifications currently applied
      const baseIndex = selectedIndex;
      const isLeft = index < baseIndex;
      const isRight = index > baseIndex;
      const distance = Math.abs(index - baseIndex);

      let scaleModification = 0;

      // Determine which scale modification applies based on the property
      if (property === 'h') {
        // For hue, the modification is calculated differently
        if (prev.scale.hue !== 0) {
          const hueShift = prev.scale.hue;
          if (isLeft) {
            scaleModification = hueShift * distance;
          } else if (isRight) {
            scaleModification = -hueShift * distance;
          }
        }
      } else if (property === 's') {
        // For saturation
        if (isLeft && prev.scale.saturationLeft !== 0) {
          scaleModification = prev.scale.saturationLeft;
        } else if (isRight && prev.scale.saturationRight !== 0) {
          scaleModification = prev.scale.saturationRight;
        }
      } else if (property === 'l') {
        // For lightness
        if (isLeft && prev.scale.lightnessLeft !== 0) {
          scaleModification = prev.scale.lightnessLeft;
        } else if (isRight && prev.scale.lightnessRight !== 0) {
          scaleModification = prev.scale.lightnessRight;
        }
      }

      // FIX: Don't clamp values when storing base values
      let baseValue;
      if (property === 'h' && value === 360) {
        // Store as a special value (like 360) that won't get normalized to 0
        baseValue = 360;
      } else if (property === 'h') {
        // For other hue values, adjust as normal
        baseValue = ((value - scaleModification) % 360 + 360) % 360;
      } else {
        // For saturation and lightness, don't clamp when storing
        // This allows values outside 0-100 range to be stored
        baseValue = value - scaleModification;
      }

      // Create the updated state
      const newState = {
        ...prev,
        individual: {
          ...prev.individual,
          [index]: {
            ...existing,
            [property]: baseValue
          }
        }
      };

      if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
        onModifiersChange(newState);
      }


      // Return the new state
      return newState;
    });
  };











  // Get the current HSL values for editing
  const getEditValues = (index, includeOriginal = false) => {

    if (index === null || index < 0 || index >= palette.length) {
      return includeOriginal
        ? { h: 0, s: 0, l: 0, original: { h: 0, s: 0, l: 0 } }
        : { h: 0, s: 0, l: 0 };
    }

    // Get the current color's HSL values from the palette - this includes ALL modifications
    const currentHSL = getExactHslString(palette[index]);

    // Get the base color's HSL values
    const baseColor = baseGeneratedPalette[index];
    const baseHSL = getExactHslString(baseColor);

    // Get individual edits if they exist
    const individualEdits = paletteModifiers.individual[index] || {};

    // Calculate scale modifications
    const baseIndex = selectedIndex;
    const isLeft = index < baseIndex;
    const isRight = index > baseIndex;
    const distance = Math.abs(index - baseIndex);

    let scaleHueModification = 0;
    let scaleSaturationModification = 0;
    let scaleLightnessModification = 0;

    // Calculate hue scale modification
    if (paletteModifiers.scale.hue !== 0) {
      const hueShift = paletteModifiers.scale.hue;
      if (isLeft) {
        scaleHueModification = hueShift * distance;
      } else if (isRight) {
        scaleHueModification = -hueShift * distance;
      }
    }

    // Calculate saturation scale modification
    if (isLeft && paletteModifiers.scale.saturationLeft !== 0) {
      scaleSaturationModification = paletteModifiers.scale.saturationLeft;
    } else if (isRight && paletteModifiers.scale.saturationRight !== 0) {
      scaleSaturationModification = paletteModifiers.scale.saturationRight;
    }

    // Calculate lightness scale modification
    if (isLeft && paletteModifiers.scale.lightnessLeft !== 0) {
      scaleLightnessModification = paletteModifiers.scale.lightnessLeft;
    } else if (isRight && paletteModifiers.scale.lightnessRight !== 0) {
      scaleLightnessModification = paletteModifiers.scale.lightnessRight;
    }

    // Calculate what the UI should show
    // Always show the current effective value (with scale effects applied)
    // This ensures sliders move when scale changes
    let displayH, displayS, displayL;

    if (individualEdits.h !== undefined) {
      // Special case for 360
      if (individualEdits.h === 360) {
        displayH = 360;
      } else {
        // For individually edited values, show the value WITH scale modifications applied
        displayH = (individualEdits.h + scaleHueModification) % 360;
        if (displayH < 0) displayH += 360;
      }
    } else {
      // Otherwise show the current value from the palette
      displayH = currentHSL.h;
    }

    if (individualEdits.s !== undefined) {
      // For individually edited values, show the value WITH scale modifications applied
      // But make sure to clamp the DISPLAY value to 0-100 range
      displayS = Math.max(0, Math.min(100, individualEdits.s + scaleSaturationModification));
    } else {
      // Otherwise show the current value from the palette
      displayS = currentHSL.s;
    }

    if (individualEdits.l !== undefined) {
      // For individually edited values, show the value WITH scale modifications applied
      // But make sure to clamp the DISPLAY value to 0-100 range
      displayL = Math.max(0, Math.min(100, individualEdits.l + scaleLightnessModification));
    } else {
      // Otherwise show the current value from the palette
      displayL = currentHSL.l;
    }

    // For standard usage (UI controls)
    if (!includeOriginal) {
      return {
        h: displayH,
        s: displayS,
        l: displayL
      };
    }

    // For when original values are needed too
    return {
      h: displayH,
      s: displayS,
      l: displayL,
      original: {
        h: baseHSL.h,
        s: baseHSL.s,
        l: baseHSL.l
      }
    };
  };



  // RESETS 

  // Reset all properties of an individual color
  const resetIndividualColor = (index) => {
    setPaletteModifiers(prev => {
      const newIndividual = { ...prev.individual };
      delete newIndividual[index];

      const newState = {
        ...prev,
        individual: newIndividual
      };

      // ADD THIS: Notify parent for URL update
      if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
        onModifiersChange(newState);
      }

      return newState;
    });
  };

  // Reset all individual color edits
  const resetAllIndividualColors = () => {
    setPaletteModifiers(prev => {
      const newState = {
        ...prev,
        individual: {}
      };

      // ADD THIS: Notify parent for URL update
      if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
        onModifiersChange(newState);
      }

      return newState;
    });
  };

  // Reset everything
  const resetAllCustomizations = () => {
    const newState = {
      scale: {
        hue: 0,
        lightnessLeft: 0,
        lightnessRight: 0,
        saturationLeft: 0,
        saturationRight: 0
      },
      individual: {}
    };

    setPaletteModifiers(newState);

    // ADD THIS: Notify parent for URL update
    if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
      onModifiersChange(newState);
    }
  };

  const resetVariableEditsOnNewColorInput = () => {
    // Reset all customizations when a new color is input
    resetAllCustomizations();
    setEditIndex(null);
  };

  // Make sure to call applyAllModifications whenever paletteModifiers change
  // useEffect(() => {
  //   console.log('⚡ useEffect triggered (line 788)', {
  //     baseGeneratedPaletteLength: baseGeneratedPalette.length,
  //     paletteModifiers,
  //     selectedIndex
  //   });

  //   if (baseGeneratedPalette.length > 0) {
  //     applyAllModifications();
  //   }
  // }, [paletteModifiers, baseGeneratedPalette, selectedIndex]); // Include selectedIndex in dependencies

  /// nienke updating this useeffect 16 feb
  useEffect(() => {
    if (isLoadingFromURL.current && selectedTailwindFamily && !hasAppliedTailwindFromURL.current) {
      return;
    }

    if (baseGeneratedPalette.length > 0) {
      applyAllModifications();
    }
  }, [paletteModifiers, baseGeneratedPalette, selectedIndex]);


  // Check if a color has effective individual edits
  const hasEffectiveChanges = (index) => {
    // If there are no individual edits at all for this index, return false immediately
    if (!paletteModifiers.individual[index]) return false;

    // Check if any property has been individually edited
    return (
      paletteModifiers.individual[index].h !== undefined ||
      paletteModifiers.individual[index].s !== undefined ||
      paletteModifiers.individual[index].l !== undefined
    );
  };

  // added this so that on page load the color does not show black or white on the color picker and squares
  useEffect(() => {
    // Mark rendering as ongoing
    isRendering.current = true;

    // Wait for all state updates and effects to settle
    setTimeout(() => {
      isRendering.current = false;
      setIsRendered(true); // Mark as fully rendered
    }, 0);
  }, []);

  const isUpdatingRef = useRef(false); // Track if the update is in progress to prevent loops

  // Get color name
  useEffect(() => {
    // Load local color names JSON
    fetch("/colorNames.json")
      .then((response) => response.json())
      .then((data) => {
        // setColorDatabase(data);

        // Create enhanced version with pre-computed RGB values
        const enhanced = {};
        Object.entries(data).forEach(([hex, name]) => {
          enhanced[hex] = {
            name,
            rgb: hexToRgb(hex)
          };
        });
        setEnhancedColorDatabase(enhanced);
      })
      .catch((error) => console.error("Error loading color names:", error));
  }, []);

  const findClosestColorForColorName = (chosenHex, enhancedDatabase) => {
    const chosenRgb = hexToRgb(chosenHex);
    let closestColor = null;
    let minDistance = Infinity;

    // Use the pre-computed RGB values
    Object.values(enhancedDatabase).forEach(({ name, rgb }) => {
      const distance = colorDistance(chosenRgb, rgb);
      if (distance < minDistance) {
        minDistance = distance;
        closestColor = name;
      }
    });

    return closestColor || "Unknown Color";
  };

  const updateColorName = useCallback(
    debounce((newColor, database) => {
      if (!newColor || Object.keys(database).length === 0) return;
      const closestName = findClosestColorForColorName(newColor, database);
      // setColorName(closestName);
      setColorName(closestName);
      setBaseColorName?.(closestName);
    }, 10), // Increased from 10ms to 300ms
    []
  );

  // useEffect(() => {
  //   if (color && Object.keys(enhancedColorDatabase).length > 0) {
  //     updateColorName(color, enhancedColorDatabase);
  //   }
  // }, [color, enhancedColorDatabase, updateColorName]);

  useEffect(() => {
    // Skip updating color name if we have a Tailwind family selected
    if (selectedTailwindFamily) return;

    if (color && Object.keys(enhancedColorDatabase).length > 0) {
      updateColorName(color, enhancedColorDatabase);
    }
  }, [color, enhancedColorDatabase, updateColorName, selectedTailwindFamily]);


  useEffect(() => {
    if (skipPaletteRegeneration) return;

    if (palette && palette.length === 11) {
      return;
    }

    if (validateHex(validColor)) {
      generatePaletteFromColor(validColor);
    }
  }, [validColor, paletteModifiers, skipPaletteRegeneration]);

  // without this all your colors are grey on page load
  useEffect(() => {
    if (isUpdatingRef.current) {
      isUpdatingRef.current = false;
      return;
    }

    // Convert currentColor to HSL
    const { h, s, l } = hexToHSL(currentColor);

    // Store precise values
    setPreciseHue(h);
    setPreciseSaturation(s);
    setPreciseLightness(l);

    // Avoid unnecessary updates (only update if values actually change)
    setHue((prev) => (prev !== h ? h : prev));
    setSaturation((prev) => (prev !== s ? s : prev));
    setLightness((prev) => (prev !== l ? l : prev));

    if (validateHex(currentColor)) {
      setValidColor(currentColor);
    }
  }, [currentColor]);


  useEffect(() => {
    if (!isRendered || isUpdatingRef.current) return;

    // Lock updates
    isUpdatingRef.current = true;

    try {
      const newColor = hslToHex(hue, saturation, lightness);

      if (newColor !== currentColor) {
        // Batch all updates together
        setCurrentColor(newColor);
        setColor(newColor);
        setValidColor(newColor);
        setInputValue(newColor);

        // Generate new palette
        const { basePalette } = generateBasePalette(newColor);
        setBaseGeneratedPalette(basePalette);
        setPalette(basePalette);

        resetVariableEditsOnNewColorInput();
      }
    } finally {
      // Always unlock when done
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 50);
    }
  }, [hue, saturation, lightness, isRendered]);

  // tailwind
  useEffect(() => {
    fetch("/tailwindColors.json")
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        setTailwindColors(data);
      })
      .catch((error) => console.error("❌ Error loading Tailwind colors:", error));
  }, []);



  // nienke also this was needed instead of the useeffect abovr
  useEffect(() => {
    if (Object.keys(tailwindColors).length > 0 &&
      selectedTailwindFamily &&
      isLoadingFromURL.current &&
      !hasAppliedTailwindFromURL.current) {
      hasAppliedTailwindFromURL.current = true;
      applyTailwindColorFamily(selectedTailwindFamily);
    }
  }, [tailwindColors, selectedTailwindFamily]);



  // added this below because 3 character hex code should also update the squares
  const expandShorthandHex = (hex) => {
    if (hex.length === 4) { // #RGB format
      return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return hex;
  };

  const handleHexInputChange = (e) => {
    resetVariableEditsOnNewColorInput();
    setSelectedTailwindFamily('');

    let newValue = e.target.value.replace(/\s+/g, '');
    const isValidHexWithoutHash = /^[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(newValue);

    // If it's a valid hex color without '#', add it
    if (isValidHexWithoutHash) {
      newValue = '#' + newValue;
    }

    setInputValue(newValue);
    setDisplayHex(newValue);

    if (validateHex(newValue)) {
      const expandedHex = expandShorthandHex(newValue);
      const { h, s, l } = hexToHSL(expandedHex);

      // Update both precise and display HSL values
      setPreciseHue(h);
      setPreciseSaturation(s);
      setPreciseLightness(l);

      setHue(h);
      setSaturation(s);
      setLightness(l);


      setValidColor(expandedHex);
      setColor(expandedHex);
      if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
        onColorChange(expandedHex);
      }
      generatePaletteFromColor(expandedHex);
      setValidHex(true);
    } else {
      // Do nothing to the palette; it keeps the previous shades
      setInputValue(newValue);
      setValidHex(false);
    }
  };





































  const generateRandomColor = () => {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
  };

  const shouldShowCustomizedValue = (isHovering, value) => {
    return isHovering || value !== 0;
  };

  // used in hue customization: has to be disabled when saturation is 0
  const hasZeroSaturation = (hexColor) => {
    try {
      // nienke leave this here (the unused h and l)
      const [h, s, l] = chroma(hexColor).hsl();
      return isNaN(s) || s < 0.001; // Consider it zero if it's very small or NaN
    } catch (e) {
      return false; // If there's an error, assume it's not zero
    }
  };

  // reset handlers
  const hasEditedSquares = () => {
    return Object.keys(paletteModifiers.individual).length > 0;
  };


  // used for handling and synchronizing base color changes
  const handleHSLValueChange = (type, newValue) => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    try {
      resetVariableEditsOnNewColorInput();

      // Update the specific HSL value
      let newHue = hue;
      let newSaturation = saturation;
      let newLightness = lightness;

      switch (type) {
        case 'hue':
          newHue = newValue / 360;
          setHue(newHue);
          break;
        case 'saturation':
          newSaturation = newValue / 100;
          setSaturation(newSaturation);
          break;
        case 'lightness':
          newLightness = newValue / 100;
          setLightness(newLightness);
          break;
      }

      // Calculate new color
      const newColor = hslToHex(newHue, newSaturation, newLightness);

      // Set all color-related states
      setCurrentColor(newColor);
      setColor(newColor);
      setValidColor(newColor);
      setInputValue(newColor);

      if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
        onColorChange(newColor);
      }

      // IMPORTANT: Generate and set palette synchronously
      const { basePalette, baseIndex } = generateBasePalette(newColor);
      setBaseGeneratedPalette(basePalette);
      setSelectedIndex(baseIndex !== -1 ? baseIndex : null);
      setPalette(basePalette); // Set palette directly

      // Apply modifications with a slight delay
      setTimeout(() => {
        if (!isUpdatingRef.current) {
          applyAllModifications();
        }
      }, 10);
    } finally {
      // Set a longer timeout to release the update lock
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    }
  };





















  // Add this effect to watch inputValue and update displayHex
  useEffect(() => {
    // Check if current inputValue is a valid color
    if (validateHex(inputValue)) {
      // Check if it can be represented as shorthand
      if (inputValue.length === 7 &&
        inputValue[1] === inputValue[2] &&
        inputValue[3] === inputValue[4] &&
        inputValue[5] === inputValue[6]) {
        // Convert to shorthand
        setDisplayHex(`#${inputValue[1]}${inputValue[3]}${inputValue[5]}`);
      } else {
        // Use the full format
        setDisplayHex(inputValue);
      }
    } else {
      // For invalid hex, just show what they typed
      setDisplayHex(inputValue);
    }
  }, [inputValue]);

  // load tailwind colors json
  useEffect(() => {
    fetch("/tailwindColors.json")
      .then((response) => response.json())
      .then((data) => {
        setTailwindColors(data);
      })
      .catch((error) => console.error("Error loading Tailwind colors:", error));
  }, []);

  // Function to apply a Tailwind color family
  const applyTailwindColorFamily = (colorFamily) => {
    if (!tailwindColors[colorFamily]) return;

    isUpdatingRef.current = true;

    setSelectedTailwindFamily(colorFamily);

    const familyColors = tailwindColors[colorFamily];
    const baseColor = familyColors['500'];

    // Reset all edits (same as the random color generation)
    // ONLY reset edits if we're NOT loading from URL
    if (!isLoadingFromURL.current) {
      resetVariableEditsOnNewColorInput();
    }

    // Set the base color
    setInputValue(baseColor);
    setValidColor(baseColor);
    setColor(baseColor);
    setValidHex(true);

    const { h, s, l } = hexToHSL(baseColor);
    setHue(h);
    setSaturation(s);
    setLightness(l);
    setPreciseHue(h);
    setPreciseSaturation(s);
    setPreciseLightness(l);

    if (typeof window !== 'undefined') {
      window._colorFamilyTracker = null;
    }

    // Create the palette directly from the Tailwind colors
    // const tailwindPalette = [
    //   familyColors['50'],
    //   familyColors['100'],
    //   familyColors['200'],
    //   familyColors['300'],
    //   familyColors['400'],
    //   familyColors['500'],
    //   familyColors['600'],
    //   familyColors['700'],
    //   familyColors['800'],
    //   familyColors['900'],
    //   familyColors['950']
    // ].map(hexColor => {
    //   // Convert to HSL format to match generateBasePalette output
    //   const { h, s, l } = hexToHSL(hexColor);
    //   return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
    // });
    /// nienke you got rid of conversion here - does it impact edited mode?
    const tailwindPalette = [
      familyColors['50'],
      familyColors['100'],
      familyColors['200'],
      familyColors['300'],
      familyColors['400'],
      familyColors['500'],
      familyColors['600'],
      familyColors['700'],
      familyColors['800'],
      familyColors['900'],
      familyColors['950']
    ];

    setBaseGeneratedPalette(tailwindPalette);
    setPalette(tailwindPalette);
    // Set base palette and regular palette
    setBaseGeneratedPalette(tailwindPalette);
    setPalette(tailwindPalette);

    // Set selected index to 5 (the 500 shade)
    setSelectedIndex(5);

    // Update the color name
    // Set the color name from the Tailwind family name
    // const tailwindColorName = colorFamily.charAt(0).toUpperCase() + colorFamily.slice(1);
    const tailwindColorName = colorFamily.substring(0, colorFamily.lastIndexOf('.'));
    setColorName(tailwindColorName);
    setBaseColorName?.(tailwindColorName);


    // Notify parent of color change
    // if (!isUpdatingFromURL && !isLoadingFromURL.current && onTailwindFamilyChange) {
    //   onTailwindFamilyChange(colorFamily);
    // }
    // if (!isUpdatingFromURL && !isLoadingFromURL.current) {
    //   if (onColorChange) {
    //     onColorChange(baseColor);
    //   }
    //   // Don't call onTailwindFamilyChange here - let onColorChange handle the URL update
    // }



    // nienke 8 mar
    if (!isUpdatingFromURL && !isLoadingFromURL.current) {
      if (onColorChange) {
        onColorChange(baseColor);
      }
      if (onTailwindFamilyChange) {
        onTailwindFamilyChange(colorFamily, baseColor); // pass baseColor as second argument
      }
    }

    // UNLOCK after state updates complete
    // setTimeout(() => {
    //   isUpdatingRef.current = false;
    // }, 50);
    setTimeout(() => {

      isUpdatingRef.current = false;

      // Manually apply modifications if loading from URL with edits
      if (isLoadingFromURL.current &&
        (Object.keys(paletteModifiers.scale).some(k => paletteModifiers.scale[k] !== 0) ||
          Object.keys(paletteModifiers.individual).length > 0)) {
        applyAllModifications();
      }

      isLoadingFromURL.current = false;
    }, 10);
  };


  return (
    <>
      {/* <div className="colorize"> */}
      <div className={'colorize ' + selectionMode}>
        <div className="selection-and-color-name">
          <p className="selection-name verify-subscription">{selectionName} COLOR SCALE</p>
          {/* <p className="color-name">&#9702;</p> */}
          <p className="color-name">&#9702; {colorName}</p>
        </div>


        {/* <p className="color-name">{colorName}</p> */}
        {/* base input hex and hsl*/}
        <div className="base-input">
          <div className="base-input--color-and-hex">

            {/* Circle Color Picker*/}
            <div className="html-picker--and--text-inputs">
              <div className="input--picker">
                <CustomColorPicker
                  color={validColor}
                  validHex={validHex}
                  onChange={(newColor) => {
                    // Prevent processing if we're currently updating
                    if (isUpdatingRef.current) return;
                    // Set updating flag
                    isUpdatingRef.current = true;
                    try {
                      // Reset any variable edits
                      resetVariableEditsOnNewColorInput();
                      // Update all color-related state
                      setInputValue(newColor);
                      setValidHex(true);
                      setValidColor(newColor);
                      setColor(newColor);
                      setCurrentColor(newColor);

                      // Update HSL values
                      const { h, s, l } = hexToHSL(newColor);
                      if (s > 0) {
                        setPreciseHue(h);
                        if (colorFormat === 'hsl') {
                          setHue(h);
                        }
                      }

                      // setPreciseHue(h);
                      setPreciseSaturation(s);
                      setPreciseLightness(l);

                      // Only set display HSL values if in HSL mode
                      if (colorFormat === 'hsl') {
                        // setHue(h);
                        setSaturation(s);
                        setLightness(l);
                      }

                      // Generate new palette
                      const { basePalette, baseIndex } = generateBasePalette(newColor);
                      setBaseGeneratedPalette(basePalette);
                      setSelectedIndex(baseIndex !== -1 ? baseIndex : null);
                      setPalette(basePalette);

                      if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                        onColorChange(newColor);
                      }

                      setSelectedTailwindFamily('');

                    } finally {
                      // Set a timeout to release the update lock
                      setTimeout(() => {
                        isUpdatingRef.current = false;
                      }, 50);
                    }
                  }}
                />

              </div>


              {/* HEX & HSL Text Input */}
              <div className="input--text">
                <div className="hex">
                  <input
                    name="hex-input"
                    type="text"
                    value={displayHex}
                    onChange={handleHexInputChange}
                    placeholder="Hexcode"
                    className="input--text--default"
                  />
                  <input
                    name="hex-input-code"
                    type="text"
                    disabled={true}
                    value={displayHex}
                    onChange={handleHexInputChange}
                    placeholder="Hexcode"
                    className="input--text--mobile"
                  />
                </div>

              </div>

              {/* Color Format Switch Button */}
              <button
                onClick={() => {
                  isUpdatingRef.current = true;
                  try {
                    // Simply toggle the format without recalculating values
                    setColorFormat(colorFormat === "hex" ? "hsl" : "hex");

                    if (colorFormat === "hex") {
                      // We're switching TO HSL format - use the stored precise values
                      setHue(preciseHue);
                      setSaturation(preciseSaturation);
                      setLightness(preciseLightness);
                    }
                    // When switching TO hex, we don't need to do anything special
                    // The current hex value remains correct
                  } finally {
                    setTimeout(() => {
                      isUpdatingRef.current = false;
                    }, 50);
                  }
                }}
                disabled={!validHex}
                className={`switch-button ${!validHex ? 'disabled' : ''}`}
              >
                <span>HSL</span>
                <svg className={colorFormat === 'hsl' ? 'dropdown-icon hsl-active' : 'dropdown-icon'} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2.41274 5.03648L2.41272 5.0365L2.41554 5.03925L7.76554 10.2643L7.99955 10.4928L8.23247 10.2631L13.5825 4.98814L13.5825 4.98815L13.5841 4.98648C13.679 4.89166 13.8179 4.89166 13.9127 4.98648C14.0073 5.08103 14.0076 5.21943 13.9136 5.31424C13.9133 5.31452 13.913 5.3148 13.9127 5.31508L8.16481 10.963L8.1648 10.963L8.16273 10.9651C8.06643 11.0614 8.02156 11.0674 7.99844 11.0674C7.93951 11.0674 7.88847 11.053 7.81869 10.9999L2.08331 5.36425C1.98932 5.26944 1.98959 5.13103 2.08414 5.03648C2.17896 4.94166 2.31791 4.94166 2.41274 5.03648Z" fill="currentColor" stroke="currentColor" strokeWidth="0.666667" />
                </svg>
              </button>

            </div>

          </div>


          {/* HSL Sliders */}
          {colorFormat === "hsl" && (
            <div className="base-input--hsl">

              <div className="hsl">

                <div className="hue">
                  <div className="hsl-text">
                    <label>H</label>
                    <input
                      type="text"
                      name="base-hue"
                      value={Math.round(hue * 360)}
                      maxLength="3"
                      pattern="[0-9]*"
                      onChange={(e) => {
                        let inputValue = e.target.value.replace(/\D/g, '');
                        inputValue = inputValue.slice(0, 3);
                        let numericValue = parseInt(inputValue, 10);
                        if (isNaN(numericValue)) numericValue = 0;
                        if (numericValue > 360) numericValue = 360;
                        handleHSLValueChange('hue', numericValue);
                        setPreciseHue(numericValue / 360);
                        setSelectedTailwindFamily('');
                      }}
                    />
                    <span>°</span>
                    <div className="arrow-btns">
                      <button
                        onClick={() => {
                          setSelectedTailwindFamily('');
                          resetVariableEditsOnNewColorInput();
                          let newValue = Math.min(360, Math.round(hue * 360) + 1);
                          setHue(newValue / 360);
                          setPreciseHue(newValue / 360);

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            const newColor = hslToHex(newValue / 360, saturation, lightness);
                            onColorChange(newColor);
                          }
                        }}
                      ></button>
                      <button
                        onClick={() => {
                          setSelectedTailwindFamily('');
                          resetVariableEditsOnNewColorInput();
                          let newValue = Math.max(0, Math.round(hue * 360) - 1);
                          setHue(newValue / 360);
                          setPreciseHue(newValue / 360);

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            const newColor = hslToHex(newValue / 360, saturation, lightness);
                            onColorChange(newColor);
                          }
                        }}
                      ></button>
                    </div>
                  </div>
                  <div className="hsl-slider">
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={Math.round(hue * 360)}

                      onChange={(e) => {
                        // Skip processing if we're currently throttled
                        if (isThrottled.current) return;
                        if (isUpdatingRef.current) return;
                        isUpdatingRef.current = true;

                        // Set throttling flag
                        isThrottled.current = true;

                        try {
                          // Update only hue
                          const newHue = parseInt(e.target.value) / 360;
                          setHue(newHue);
                          setPreciseHue(newHue);
                          // Calculate new color
                          const newColor = hslToHex(newHue, saturation, lightness);

                          // Update hex values
                          setCurrentColor(newColor);
                          setColor(newColor);
                          setValidColor(newColor);
                          setInputValue(newColor);

                          const { basePalette, baseIndex } = generateBasePalette(newColor);
                          setBaseGeneratedPalette(basePalette);
                          setSelectedIndex(baseIndex !== -1 ? baseIndex : null);
                          setPalette(basePalette); // Directly set palette

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            onColorChange(newColor);
                            setSelectedTailwindFamily('');
                          }

                          // Force a palette update
                          setTimeout(() => {
                            applyAllModifications();
                          }, 10);
                        } finally {
                          setTimeout(() => {
                            isThrottled.current = false;
                            isUpdatingRef.current = false;
                          }, 100);
                        }
                      }}


                      onMouseDown={() => {
                        // Only reset if we're starting a new drag
                        if (!isBaseHueSliderMoving) {
                          resetVariableEditsOnNewColorInput();
                          setIsBaseHueSliderMoving(true);
                        }
                      }}
                      onMouseUp={() => {
                        setIsBaseHueSliderMoving(false);
                      }}
                      onTouchStart={() => {
                        if (!isBaseHueSliderMoving) {
                          resetVariableEditsOnNewColorInput();
                          setIsBaseHueSliderMoving(true);
                        }
                      }}
                      onTouchEnd={() => {
                        setIsBaseHueSliderMoving(false);
                      }}

                      className="w-full"
                      style={{ backgroundImage: 'linear-gradient(to right, rgb(236, 9, 9) 0%, rgb(236, 236, 9) 17%, rgb(9, 236, 9) 33%, rgb(9, 236, 236) 50%, rgb(9, 9, 236) 67%, rgb(236, 9, 236) 83%, rgb(236, 9, 9) 100%)' }}
                    />
                  </div>
                </div>

                <div className="saturation">
                  <div className="hsl-text">
                    <label>S</label>
                    <input
                      type="text"
                      name="base-saturation"
                      value={Math.round(saturation * 100)}
                      maxLength="3"
                      pattern="[0-9]*"

                      onChange={(e) => {
                        let inputValue = e.target.value.replace(/\D/g, '');
                        inputValue = inputValue.slice(0, 3);
                        let numericValue = parseInt(inputValue, 10);
                        if (isNaN(numericValue)) numericValue = 0;
                        if (numericValue > 100) numericValue = 100;
                        handleHSLValueChange('saturation', numericValue);
                        setPreciseSaturation(numericValue / 100);
                        setSelectedTailwindFamily('');
                      }}
                    />
                    <span>%</span>
                    <div className="arrow-btns">
                      <button
                        onClick={() => {
                          setSelectedTailwindFamily('');
                          resetVariableEditsOnNewColorInput();
                          let newValue = Math.min(100, Math.round(saturation * 100) + 1);
                          setSaturation(newValue / 100);
                          setPreciseSaturation(newValue / 100);

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            const newColor = hslToHex(hue, newValue / 100, lightness);
                            onColorChange(newColor);
                          }
                        }}
                      ></button>
                      <button
                        onClick={() => {
                          setSelectedTailwindFamily('');
                          resetVariableEditsOnNewColorInput();
                          let newValue = Math.max(0, Math.round(saturation * 100) - 1);
                          setSaturation(newValue / 100);
                          setPreciseSaturation(newValue / 100);

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            const newColor = hslToHex(hue, newValue / 100, lightness);
                            onColorChange(newColor);
                          }
                        }}
                      ></button>
                    </div>
                  </div>
                  <div className="hsl-slider">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round(saturation * 100)}

                      onChange={(e) => {
                        // Skip processing if we're currently throttled
                        if (isThrottled.current) return;
                        if (isUpdatingRef.current) return;
                        isUpdatingRef.current = true;

                        // Set throttling flag
                        isThrottled.current = true;

                        try {
                          // Update only hue
                          const newSat = parseInt(e.target.value) / 100;
                          setSaturation(newSat);
                          setPreciseSaturation(newSat);

                          // Calculate new color
                          const newColor = hslToHex(hue, newSat, lightness);

                          // Update hex values
                          setCurrentColor(newColor);
                          setColor(newColor);
                          setValidColor(newColor);
                          setInputValue(newColor);

                          const { basePalette, baseIndex } = generateBasePalette(newColor);
                          setBaseGeneratedPalette(basePalette);
                          setSelectedIndex(baseIndex !== -1 ? baseIndex : null);
                          setPalette(basePalette); // Directly set palette

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            onColorChange(newColor);
                            setSelectedTailwindFamily('');
                          }

                          // Force a palette update
                          setTimeout(() => {
                            applyAllModifications();
                          }, 10);
                        } finally {
                          setTimeout(() => {
                            isThrottled.current = false;
                            isUpdatingRef.current = false;
                          }, 100);
                        }
                      }}



                      onMouseDown={() => {
                        // Only reset if we're starting a new drag
                        if (!isBaseSaturationSliderMoving) {
                          resetVariableEditsOnNewColorInput();
                          setIsBaseSaturationSliderMoving(true);
                        }
                      }}
                      onMouseUp={() => {
                        setIsBaseSaturationSliderMoving(false);
                      }}
                      onTouchStart={() => {
                        if (!isBaseSaturationSliderMoving) {
                          resetVariableEditsOnNewColorInput();
                          setIsBaseSaturationSliderMoving(true);
                        }
                      }}
                      onTouchEnd={() => {
                        setIsBaseSaturationSliderMoving(false);
                      }}
                      className="w-full"
                      style={{ backgroundImage: `linear-gradient(to right, rgb(122, 122, 122) 0%, ${palette[5]})` }}
                    />
                  </div>
                </div>


                <div className="lightness">
                  <div className="hsl-text">
                    <label>L</label>
                    <input
                      type="text"
                      name="base-lightness"
                      value={Math.round(lightness * 100)}
                      maxLength="3"
                      pattern="[0-9]*"
                      onChange={(e) => {
                        let inputValue = e.target.value.replace(/\D/g, '');
                        inputValue = inputValue.slice(0, 3);
                        let numericValue = parseInt(inputValue, 10);
                        if (isNaN(numericValue)) numericValue = 0;
                        if (numericValue > 100) numericValue = 100;
                        handleHSLValueChange('lightness', numericValue);
                        setPreciseLightness(numericValue / 100);
                        setSelectedTailwindFamily('');
                      }}
                    />
                    <span>%</span>
                    <div className="arrow-btns">
                      <button
                        onClick={() => {
                          setSelectedTailwindFamily('');
                          resetVariableEditsOnNewColorInput();
                          let newValue = Math.min(100, Math.round(lightness * 100) + 1);
                          setLightness(newValue / 100);
                          setPreciseLightness(newValue / 100);

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            const newColor = hslToHex(hue, saturation, newValue / 100);
                            onColorChange(newColor);
                          }
                        }}
                      ></button>
                      <button
                        onClick={() => {
                          setSelectedTailwindFamily('');
                          resetVariableEditsOnNewColorInput();
                          let newValue = Math.max(0, Math.round(lightness * 100) - 1);
                          setLightness(newValue / 100);
                          setPreciseLightness(newValue / 100);

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            const newColor = hslToHex(hue, saturation, newValue / 100);
                            onColorChange(newColor);
                          }
                        }}
                      ></button>
                    </div>
                  </div>
                  <div className="hsl-slider">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round(lightness * 100)}

                      onChange={(e) => {
                        // Skip processing if we're currently throttled
                        if (isThrottled.current) return;
                        if (isUpdatingRef.current) return;
                        isUpdatingRef.current = true;

                        // Set throttling flag
                        isThrottled.current = true;

                        try {
                          // Update only hue
                          const newLig = parseInt(e.target.value) / 100;
                          setLightness(newLig);
                          setPreciseLightness(newLig);

                          // Calculate new color
                          const newColor = hslToHex(hue, saturation, newLig);

                          // Update hex values
                          setCurrentColor(newColor);
                          setColor(newColor);
                          setValidColor(newColor);
                          setInputValue(newColor);

                          const { basePalette, baseIndex } = generateBasePalette(newColor);
                          setBaseGeneratedPalette(basePalette);
                          setSelectedIndex(baseIndex !== -1 ? baseIndex : null);
                          setPalette(basePalette); // Directly set palette

                          if (!isUpdatingFromURL && !isLoadingFromURL.current && onColorChange) {
                            onColorChange(newColor);
                            setSelectedTailwindFamily('');
                          }

                          // Force a palette update
                          setTimeout(() => {
                            applyAllModifications();
                          }, 10);
                        } finally {
                          setTimeout(() => {
                            isThrottled.current = false;
                            isUpdatingRef.current = false;
                          }, 100);
                        }
                      }}



                      onMouseDown={() => {
                        // Only reset if we're starting a new drag
                        if (!isBaseLightnessSliderMoving) {
                          resetVariableEditsOnNewColorInput();
                          setIsBaseLightnessSliderMoving(true);
                        }
                      }}
                      onMouseUp={() => {
                        setIsBaseLightnessSliderMoving(false);
                      }}
                      onTouchStart={() => {
                        if (!isBaseLightnessSliderMoving) {
                          resetVariableEditsOnNewColorInput();
                          setIsBaseLightnessSliderMoving(true);
                        }
                      }}
                      onTouchEnd={() => {
                        setIsBaseLightnessSliderMoving(false);
                      }}
                      className="w-full"
                      style={{ backgroundImage: `linear-gradient(to right, rgb(0, 0, 0) 0%, ${palette[5]}, rgb(255, 255, 255) 100%)` }}
                    />
                  </div>
                </div>

              </div>


            </div>
          )}
        </div>

        {/* tools */}
        <div className="tools">
          {/* colorize me */}
          <button className="action pri-btn svg---text generate-random-color-btn"
            onClick={() => {
              resetVariableEditsOnNewColorInput();
              const randomColor = generateRandomColor();
              setInputValue(randomColor);
              setValidColor(randomColor);
              setColor(randomColor);
              setValidHex(true);
              const { h, s, l } = hexToHSL(randomColor);
              setHue(h);
              setSaturation(s);
              setLightness(l);

              setPreciseHue(h);
              setPreciseSaturation(s);
              setPreciseLightness(l);
              if (typeof window !== 'undefined') {
                window._colorFamilyTracker = null;
              }
              generatePaletteFromColor(randomColor);
              if (!isUpdatingFromURL && onColorChange) {
                onColorChange(randomColor);
              }
              setSelectedTailwindFamily('');
            }}
          >
            <svg width={23} height={23} className="random-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#ffffff">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" />
            </svg>

            <span>Generate {selectionName.toLowerCase()} color</span>
          </button>



          {/* taiwind families */}
          <div className="action-tailwind-container">
            <button
              onClick={() => setShowTailwindDropdown(!showTailwindDropdown)}
              className={`action svg---text ${showTailwindDropdown ? 'active' : ''}`}
            >
              <svg fill="currentColor" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M12 6.036c-2.667 0-4.333 1.325-5 3.976 1-1.325 2.167-1.822 3.5-1.491.761.189 1.305.738 1.906 1.345C13.387 10.855 14.522 12 17 12c2.667 0 4.333-1.325 5-3.976-1 1.325-2.166 1.822-3.5 1.491-.761-.189-1.305-.738-1.907-1.345-.98-.99-2.114-2.134-4.593-2.134zM7 12c-2.667 0-4.333 1.325-5 3.976 1-1.326 2.167-1.822 3.5-1.491.761.189 1.305.738 1.907 1.345.98.989 2.115 2.134 4.594 2.134 2.667 0 4.333-1.325 5-3.976-1 1.325-2.167 1.822-3.5 1.491-.761-.189-1.305-.738-1.906-1.345C10.613 13.145 9.478 12 7 12z" /></svg>
              <span>Tailwind colors</span>

              <span className="toggle-tailwind-btn">
                <svg className="dropdown-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2.41274 5.03648L2.41272 5.0365L2.41554 5.03925L7.76554 10.2643L7.99955 10.4928L8.23247 10.2631L13.5825 4.98814L13.5825 4.98815L13.5841 4.98648C13.679 4.89166 13.8179 4.89166 13.9127 4.98648C14.0073 5.08103 14.0076 5.21943 13.9136 5.31424C13.9133 5.31452 13.913 5.3148 13.9127 5.31508L8.16481 10.963L8.1648 10.963L8.16273 10.9651C8.06643 11.0614 8.02156 11.0674 7.99844 11.0674C7.93951 11.0674 7.88847 11.053 7.81869 10.9999L2.08331 5.36425C1.98932 5.26944 1.98959 5.13103 2.08414 5.03648C2.17896 4.94166 2.31791 4.94166 2.41274 5.03648Z" fill="currentColor" stroke="currentColor" strokeWidth="0.666667" />
                </svg>
              </span>
            </button>

            {showTailwindDropdown && (
              <div className={`tailwind-selector ${showTailwindDropdown ? 'active' : ''}`}>
                <div className="tailwind-version-toggle">
                  <button
                    className={tailwindVersion === 'v3' ? 'active' : ''}
                    onClick={() => setTailwindVersion('v3')}
                  >
                    v3
                  </button>
                  <button
                    className={tailwindVersion === 'v4' ? 'active' : ''}
                    onClick={() => setTailwindVersion('v4')}
                  >
                    v4
                  </button>
                </div>

                <div className="tailwind-options">
                  {Object.keys(tailwindColors)
                    .filter(colorFamily => colorFamily.includes(tailwindVersion))
                    .map((colorFamily) => (
                      <label key={colorFamily} className="tailwind-color-option">
                        <input
                          type="radio"
                          name="tailwindFamily"
                          value={colorFamily}
                          checked={selectedTailwindFamily === colorFamily}
                          onChange={() => applyTailwindColorFamily(colorFamily)}
                        />
                        <div
                          className="color-preview"
                          style={{ backgroundColor: tailwindColors[colorFamily]['500'] }}
                        />
                        <span className="color-family-name">
                          {colorFamily.substring(0, colorFamily.lastIndexOf('.'))}
                        </span>
                      </label>
                    ))}
                </div>
              </div>

            )}
          </div>



          {/* export */}
          <button className="action action--export svg---text" onClick={onOpenExport} >
            <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 9.75 16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
            </svg>
            <span className="export" colors={palette}>Export code</span>
          </button>
          {/* figma export */}
          {/* className="action action--export-to-figma svg---text" */}
          <button className={`action action--export-to-figma svg---text ${!subscriptionActive && 'guard'}`} onClick={() => executeMembershipGuard(onOpenExportToFigma)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none"><path fill="currentColor" fillRule="evenodd" d="M3 3.62C3 2.172 4.205 1 5.691 1h4.618C11.795 1 13 2.173 13 3.62a2.6 2.6 0 01-1.215 2.19A2.6 2.6 0 0113 8c0 1.447-1.205 2.62-2.691 2.62h-.059a2.723 2.723 0 01-1.81-.681v2.413C8.44 13.818 7.205 15 5.707 15 4.223 15 3 13.83 3 12.38a2.6 2.6 0 011.215-2.19A2.6 2.6 0 013 8a2.6 2.6 0 011.215-2.19A2.6 2.6 0 013 3.62zm4.56 2.619H5.69c-1 0-1.81.788-1.81 1.761 0 .97.805 1.755 1.799 1.761h1.879V6.24zM8.44 8c0 .973.811 1.761 1.81 1.761h.059c1 0 1.81-.788 1.81-1.76 0-.974-.81-1.762-1.81-1.762h-.059c-.999 0-1.81.788-1.81 1.761zm-2.749 2.62H5.68c-.994.005-1.798.792-1.798 1.76 0 .97.82 1.762 1.824 1.762 1.019 0 1.853-.805 1.853-1.79V10.62H5.691zm0-8.762H7.56v3.523H5.691c-1 0-1.81-.789-1.81-1.762 0-.972.81-1.761 1.81-1.761zm2.75 3.523V1.858h1.868c1 0 1.81.789 1.81 1.761 0 .973-.81 1.762-1.81 1.762H8.44z" clipRule="evenodd" />
            </svg>
            <span>Export to Figma</span>
          </button>
          {/* contrast grid */}
          <button className={`action action--contrast-grid svg---text ${!subscriptionActive && 'guard'}`} onClick={() => executeMembershipGuard(onOpenContrastGrid)}>
            <svg width={22} height={22} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
              <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10">
                <circle cx="16" cy="16" r="13" />
                <line x1="16" y1="8" x2="26" y2="8" />
                <line x1="16" y1="24" x2="26" y2="24" />
                <line x1="16" y1="16" x2="29" y2="16" />
                <line x1="16" y1="12" x2="28" y2="12" />
                <line x1="16" y1="20" x2="28" y2="20" />
              </g>
            </svg>
            <span>Contrast grid</span>
          </button>
          {/* save */}
          <button className={`action action--save svg---text ${!subscriptionActive && 'guard'}`} onClick={() => executeMembershipGuard(onSave)}>
            <svg style={{ marginTop: '-2px' }} xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M19 8.81818C19 6.70946 17.1345 5 14.8333 5C13.1128 5 11.6358 5.95563 11 7.31927C10.3642 5.95563 8.88722 5 7.16667 5C4.86548 5 3 6.70946 3 8.81818C3 14.9447 11 19 11 19C11 19 19 14.9447 19 8.81818Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Save</span>
          </button>
          {/* edit */}
          <div
            className={`action-edit-container 
              ${(paletteModifiers.scale.hue !== 0 ||
                paletteModifiers.scale.saturationLeft !== 0 ||
                paletteModifiers.scale.saturationRight !== 0 ||
                paletteModifiers.scale.lightnessLeft !== 0 ||
                paletteModifiers.scale.lightnessRight !== 0 || hasEditedSquares())
              && 'show-reset-btn'
              }
            `}
          >
            <button
              onClick={() => executeMembershipGuard(() => setShowEditor(!showEditor))}
              className={`action svg---text 
              ${!subscriptionActive && 'guard'}
              ${showEditor ? ' active' : ''}
              ${(paletteModifiers.scale.hue !== 0 ||
                  paletteModifiers.scale.saturationLeft !== 0 ||
                  paletteModifiers.scale.saturationRight !== 0 ||
                  paletteModifiers.scale.lightnessLeft !== 0 ||
                  paletteModifiers.scale.lightnessRight !== 0 || hasEditedSquares())
                && 'show-reset-btn'
                }
            `}>
              <svg width={22} height={22} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
              </svg>
              <span>Edit</span>



              {/* <span className="toggle-edit-btn"
                onClick={() => executeMembershipGuard(() => setShowEditor(!showEditor))}
              > */}
              <span className="toggle-edit-btn">
                <svg className="dropdown-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2.41274 5.03648L2.41272 5.0365L2.41554 5.03925L7.76554 10.2643L7.99955 10.4928L8.23247 10.2631L13.5825 4.98814L13.5825 4.98815L13.5841 4.98648C13.679 4.89166 13.8179 4.89166 13.9127 4.98648C14.0073 5.08103 14.0076 5.21943 13.9136 5.31424C13.9133 5.31452 13.913 5.3148 13.9127 5.31508L8.16481 10.963L8.1648 10.963L8.16273 10.9651C8.06643 11.0614 8.02156 11.0674 7.99844 11.0674C7.93951 11.0674 7.88847 11.053 7.81869 10.9999L2.08331 5.36425C1.98932 5.26944 1.98959 5.13103 2.08414 5.03648C2.17896 4.94166 2.31791 4.94166 2.41274 5.03648Z" fill="currentColor" stroke="currentColor" strokeWidth="0.666667" />
                </svg>
              </span>


            </button>

            <div
              className={`reset reset--all-edits`}
              onClick={(e) => {
                e.stopPropagation();
                resetAllCustomizations()
              }}>
              Reset all edits
            </div>
          </div>




        </div>


        {showEditor &&
          <div className="editor">

            <div className="edit-scale-vs-edit-variable">
              <button onClick={() => {
                setShowEditScale(false);
                setShowEditVariable(true);
              }}
                className={`${showEditVariable ? ' active' : ''} ${hasEditedSquares() && 'has-edits'}`}>
                <span>Edit variable</span>
              </button>
              <button onClick={() => {
                setShowEditScale(true);
                setShowEditVariable(false);
              }}
                className={`${showEditScale ? ' active' : ''}
                  ${(paletteModifiers.scale.hue !== 0 ||
                    paletteModifiers.scale.saturationLeft !== 0 ||
                    paletteModifiers.scale.saturationRight !== 0 ||
                    paletteModifiers.scale.lightnessLeft !== 0 ||
                    paletteModifiers.scale.lightnessRight !== 0)
                  && 'has-edits'}
                `}>
                <span>Edit scale</span>
              </button>

            </div>

            {/* edit scale */}
            {showEditScale &&
              <div className="edit-scale--container">

                <div className="edit-scale--handlers">
                  <label className={`${editScaleMode === 'hue' ? 'active' : ''} ${paletteModifiers.scale.hue !== 0 ? 'scale-is-edited' : ''}`}>
                    <input
                      type="radio"
                      name="editScaleMode"
                      value="hue"
                      checked={editScaleMode === 'hue'}
                      onChange={() => setEditScaleMode('hue')}
                    />
                    <span>Hue</span>
                  </label>
                  <label className={`${editScaleMode === 'saturation' ? 'active' : ''} ${paletteModifiers.scale.saturationLeft === 0 && paletteModifiers.scale.saturationRight === 0 ? '' : 'scale-is-edited'}`}>
                    <input
                      type="radio"
                      name="editScaleMode"
                      value="saturation"
                      checked={editScaleMode === 'saturation'}
                      onChange={() => setEditScaleMode('saturation')}
                    />
                    <span>Saturation</span>
                  </label>
                  <label className={`${editScaleMode === 'lightness' ? 'active' : ''} ${paletteModifiers.scale.lightnessLeft === 0 && paletteModifiers.scale.lightnessRight === 0 ? '' : 'scale-is-edited'}`}>
                    <input
                      type="radio"
                      name="editScaleMode"
                      value="lightness"
                      checked={editScaleMode === 'lightness'}
                      onChange={() => setEditScaleMode('lightness')}
                    />
                    <span>Lightness</span>
                  </label>
                </div>


                {/* hue shift */}
                <div className={`edit-scale--container-mode ${editScaleMode === 'hue' ? 'active' : ''}`}>
                  <div className={`edit-scale ${hasZeroSaturation(validColor) && 'disabled'}`}>
                    <label htmlFor="edit-scale--hue-shift">Hue shift</label>
                    <input
                      id="edit-scale--hue-shift"
                      type="range"
                      min="-10"
                      max="10"
                      step="1"
                      value={paletteModifiers.scale.hue}
                      disabled={hasZeroSaturation(validColor)}
                      onMouseUp={() => {
                        isHueSliderMoving.current = false;
                        // Clear snapshot if returning to zero
                        if (paletteModifiers.scale.hue === 0) {
                          originalHuesSnapshot.current = null;
                        }
                      }}
                      onMouseLeave={() => {
                        isHueSliderMoving.current = false;
                        setHueHover(false);
                      }}
                      onTouchEnd={() => {
                        setHueHover(false);
                        isHueSliderMoving.current = false;
                        if (paletteModifiers.scale.hue === 0) {
                          originalHuesSnapshot.current = null;
                        }
                      }}

                      onMouseEnter={() => setHueHover(true)}
                      onTouchStart={() => setHueHover(true)}

                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        updateScaleModifier('hue', newValue);
                      }}
                    />
                    {shouldShowCustomizedValue(hueHover, paletteModifiers.scale.hue) ?
                      <div className="value">
                        {paletteModifiers.scale.hue}
                      </div> : null}
                  </div>
                  <button className="reset reset--hue-shift"
                    disabled={paletteModifiers.scale.hue === 0 ? true : false}
                    onClick={() => {
                      // setPaletteModifiers(prev => ({
                      //   ...prev,
                      //   scale: {
                      //     ...prev.scale,
                      //     hue: 0
                      //   }
                      // }));
                      const newModifiers = {
                        ...paletteModifiers,
                        scale: {
                          ...paletteModifiers.scale,
                          hue: 0
                        }
                      };
                      setPaletteModifiers(newModifiers);

                      // ADD THIS: Notify parent for URL update
                      if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
                        onModifiersChange(newModifiers);
                      }
                    }}
                  >
                    <span>Reset hue</span>
                  </button>
                </div>

                {/* lightness shifts */}
                <div className={`edit-scale--container-mode ${editScaleMode === 'lightness' ? 'active' : ''}`}>
                  <div className={`edit-scale ${selectedIndex === 0 && 'disabled'}`}>
                    <label htmlFor="edit-scale--lightness-left">
                      {selectedIndex > 0
                        ? `Variable ${squareNames[0]} - ${squareNames[selectedIndex - 1]}`
                        : 'Lightness before Base'}
                    </label>
                    <input
                      id="edit-scale--lightness-left"
                      type="range"
                      min="-10"
                      max="10"
                      step="1"
                      value={paletteModifiers.scale.lightnessLeft}
                      onMouseEnter={() => setLightLeftHover(true)}
                      onMouseLeave={() => setLightLeftHover(false)}
                      onTouchStart={() => setLightLeftHover(true)}
                      onTouchEnd={() => setLightLeftHover(false)}
                      disabled={selectedIndex === 0}

                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        updateScaleModifier('lightnessLeft', newValue);
                      }}

                    />

                    {shouldShowCustomizedValue(lightLeftHover, paletteModifiers.scale.lightnessLeft) ?
                      <div className="value">
                        {paletteModifiers.scale.lightnessLeft}
                      </div> : null}
                  </div>
                  <div className={`edit-scale ${selectedIndex === palette.length - 1 && 'disabled'}`}>
                    <label htmlFor="edit-scale--lightness-right">
                      {selectedIndex < palette.length - 1
                        ? `Variable ${squareNames[selectedIndex + 1]} - ${squareNames[palette.length - 1]}`
                        : 'Lightness after Base'}
                    </label>
                    <input
                      id="edit-scale--lightness-right"
                      type="range"
                      min="-10"
                      max="10"
                      step="1"
                      value={paletteModifiers.scale.lightnessRight}
                      onMouseEnter={() => setLightRightHover(true)}
                      onMouseLeave={() => setLightRightHover(false)}
                      onTouchStart={() => setLightRightHover(true)}
                      onTouchEnd={() => setLightRightHover(false)}
                      disabled={selectedIndex === palette.length - 1}

                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        updateScaleModifier('lightnessRight', newValue);
                      }}

                    />
                    {shouldShowCustomizedValue(lightRightHover, paletteModifiers.scale.lightnessRight) ?
                      <div className="value">
                        {paletteModifiers.scale.lightnessRight}
                      </div> : null}
                  </div>
                  <button className="reset reset--lightness-shift"
                    disabled={paletteModifiers.scale.lightnessLeft === 0 && paletteModifiers.scale.lightnessRight === 0}
                    onClick={() => {
                      // setPaletteModifiers(prev => ({
                      //   ...prev,
                      //   scale: {
                      //     ...prev.scale,
                      //     lightnessLeft: 0,
                      //     lightnessRight: 0
                      //   }
                      // }));
                      const newModifiers = {
                        ...paletteModifiers,
                        scale: {
                          ...paletteModifiers.scale,
                          lightnessLeft: 0,
                          lightnessRight: 0
                        }
                      };
                      setPaletteModifiers(newModifiers);

                      // ADD THIS: Notify parent for URL update
                      if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
                        onModifiersChange(newModifiers);
                      }
                    }}
                  >
                    <span>Reset lightness</span>
                  </button>
                </div>

                {/* saturation shifts */}
                <div className={`edit-scale--container-mode ${editScaleMode === 'saturation' ? 'active' : ''}`}>
                  <div className={`edit-scale ${selectedIndex === 0 && 'disabled'}`}>
                    <label htmlFor="edit-scale--saturation-left">
                      {selectedIndex > 0
                        ? `Variable ${squareNames[0]} - ${squareNames[selectedIndex - 1]}`
                        : 'Saturation before Base'}
                    </label>
                    <input
                      id="edit-scale--saturation-left"
                      type="range"
                      min="-10"
                      max="10"
                      step="1"
                      value={paletteModifiers.scale.saturationLeft}
                      onMouseEnter={() => setSatLeftHover(true)}
                      onMouseLeave={() => setSatLeftHover(false)}
                      onTouchStart={() => setSatLeftHover(true)}
                      onTouchEnd={() => setSatLeftHover(false)}
                      disabled={selectedIndex === 0}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        updateScaleModifier('saturationLeft', newValue);
                      }}

                    />
                    {shouldShowCustomizedValue(satLeftHover, paletteModifiers.scale.saturationLeft) ?
                      <div className="value">
                        {paletteModifiers.scale.saturationLeft}
                      </div> : null}
                  </div>
                  <div className={`edit-scale ${selectedIndex === palette.length - 1 && 'disabled'}`}>
                    <label htmlFor="edit-scale--saturation-right">
                      {selectedIndex < palette.length - 1
                        ? `Variable ${squareNames[selectedIndex + 1]} - ${squareNames[palette.length - 1]}`
                        : 'Saturation after Base'}
                    </label>
                    <input
                      id="edit-scale--saturation-right"
                      type="range"
                      min="-10"
                      max="10"
                      step="1"
                      value={paletteModifiers.scale.saturationRight}
                      onMouseEnter={() => setSatRightHover(true)}
                      onMouseLeave={() => setSatRightHover(false)}
                      onTouchStart={() => setSatRightHover(true)}
                      onTouchEnd={() => setSatRightHover(false)}
                      disabled={selectedIndex === palette.length - 1}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        updateScaleModifier('saturationRight', newValue);
                      }}

                    />
                    {shouldShowCustomizedValue(satRightHover, paletteModifiers.scale.saturationRight) ?
                      <div className="value">
                        {paletteModifiers.scale.saturationRight}
                      </div> : null}
                  </div>
                  <button className="reset reset--saturation-shift"
                    disabled={paletteModifiers.scale.saturationLeft === 0 && paletteModifiers.scale.saturationRight === 0}
                    onClick={() => {
                      // setPaletteModifiers(prev => ({
                      //   ...prev,
                      //   scale: {
                      //     ...prev.scale,
                      //     saturationLeft: 0,
                      //     saturationRight: 0
                      //   }
                      // }));
                      const newModifiers = {
                        ...paletteModifiers,
                        scale: {
                          ...paletteModifiers.scale,
                          saturationLeft: 0,
                          saturationRight: 0
                        }
                      };
                      setPaletteModifiers(newModifiers);

                      // ADD THIS: Notify parent for URL update
                      if (!isUpdatingFromURL && !isLoadingFromURL.current && onModifiersChange) {
                        onModifiersChange(newModifiers);
                      }
                    }}
                  >
                    <span>Reset saturation</span>
                  </button>
                </div>
              </div>
            }


            {showEditVariable &&
              <div className="edit-variable--container">
                {palette.map((shade, idx,) => {
                  return (
                    <div
                      key={idx}
                      className={`edit-variable ${selectedIndex === idx ? 'picked-color' : ''} ${editIndex === idx ? 'edit-indiv' : ''} ${hasEffectiveChanges(idx) ? 'variable-is-edited' : ''}`}
                      onClick={() => {
                        if (editIndex !== idx && selectedIndex !== idx) {
                          setEditIndex(idx);
                        } else {
                          if (selectedIndex !== idx) {
                            setEditIndex(null);
                          }
                        }
                      }}
                    >
                      <div className="edit-variable--content" style={{
                        backgroundColor: palette[idx],
                        color: getContrastRatio(palette[idx], '#ffffff') > 2.0
                          ? '#ffffff' : '#211f1c'
                      }}>
                        <p className="variable-name">{squareNames[idx]}</p>
                        <svg className="dropdown-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2.41274 5.03648L2.41272 5.0365L2.41554 5.03925L7.76554 10.2643L7.99955 10.4928L8.23247 10.2631L13.5825 4.98814L13.5825 4.98815L13.5841 4.98648C13.679 4.89166 13.8179 4.89166 13.9127 4.98648C14.0073 5.08103 14.0076 5.21943 13.9136 5.31424C13.9133 5.31452 13.913 5.3148 13.9127 5.31508L8.16481 10.963L8.1648 10.963L8.16273 10.9651C8.06643 11.0614 8.02156 11.0674 7.99844 11.0674C7.93951 11.0674 7.88847 11.053 7.81869 10.9999L2.08331 5.36425C1.98932 5.26944 1.98959 5.13103 2.08414 5.03648C2.17896 4.94166 2.31791 4.94166 2.41274 5.03648Z" fill="currentColor" stroke="currentColor" strokeWidth="0.666667" />
                        </svg>
                        {selectedIndex === idx &&
                          <svg className="lock-icon" width="18" height="18" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                          </svg>
                        }
                      </div>

                      <div>
                        <div className={`hsl-container ${editIndex === idx ? 'show' : ''} `}
                          ref={editRef} onClick={(e) => e.stopPropagation()}
                        >

                          <div className="hsl">
                            {/* hue */}
                            <div>
                              <div className="hsl-text">
                                <label>H</label>
                                <input
                                  type="text"
                                  name="edit-hue"
                                  value={Math.round(getEditValues(editIndex).h)}
                                  maxLength="3"
                                  pattern="[0-9]*"
                                  onChange={(e) => {
                                    let inputValue = e.target.value.replace(/\D/g, '');
                                    inputValue = inputValue.slice(0, 3);

                                    let numericValue = parseInt(inputValue, 10);
                                    if (isNaN(numericValue) || numericValue < 0 || numericValue > 360) {
                                      numericValue = 0; // Default to 0 if out of bounds
                                    }
                                    updateIndividualColor(editIndex, 'h', numericValue);
                                  }}
                                />
                                <span>°</span>
                                <div className="arrow-btns">
                                  <button
                                    onClick={() => {
                                      let newValue = Math.min(360, Math.round(getEditValues(editIndex).h) + 1);
                                      updateIndividualColor(editIndex, 'h', newValue);
                                    }}
                                  ></button>
                                  <button
                                    onClick={() => {
                                      let newValue = Math.max(0, Math.round(getEditValues(editIndex).h) - 1);
                                      updateIndividualColor(editIndex, 'h', newValue);
                                    }}
                                  ></button>
                                </div>
                              </div>
                              <div className="hsl-slider">
                                <input
                                  type="range"
                                  min="0"
                                  max="360"
                                  style={{ backgroundImage: 'linear-gradient(to right, rgb(236, 9, 9) 0%, rgb(236, 236, 9) 17%, rgb(9, 236, 9) 33%, rgb(9, 236, 236) 50%, rgb(9, 9, 236) 67%, rgb(236, 9, 236) 83%, rgb(236, 9, 9) 100%)' }}
                                  value={Math.round(getEditValues(editIndex).h)}
                                  onChange={(e) => {
                                    // Skip processing if we're currently throttled
                                    if (isThrottled.current) return;
                                    // Set throttling flag
                                    isThrottled.current = true;
                                    const newValue = parseInt(e.target.value);
                                    updateIndividualColor(editIndex, 'h', newValue);
                                    setTimeout(() => {
                                      isThrottled.current = false;
                                    }, 10);
                                  }}
                                />
                              </div>
                            </div>

                            {/* saturation */}
                            <div>
                              <div className="hsl-text">
                                <label>S</label>
                                <input
                                  type="text"
                                  name="edit-saturation"
                                  value={Math.round(getEditValues(editIndex).s)}
                                  maxLength="3"
                                  pattern="[0-9]*"
                                  onChange={(e) => {
                                    let inputValue = e.target.value.replace(/\D/g, '');
                                    inputValue = inputValue.slice(0, 3);

                                    let numericValue = parseInt(inputValue, 10);
                                    if (isNaN(numericValue) || numericValue < 0 || numericValue > 100) {
                                      numericValue = 0;
                                    }
                                    updateIndividualColor(editIndex, 's', numericValue);
                                  }}
                                />
                                <span>%</span>
                                <div className="arrow-btns">
                                  <button
                                    onClick={() => {
                                      let newValue = Math.min(100, Math.round(getEditValues(editIndex).s) + 1);
                                      updateIndividualColor(editIndex, 's', newValue);
                                    }}
                                  ></button>
                                  <button
                                    onClick={() => {
                                      let newValue = Math.max(0, Math.round(getEditValues(editIndex).s) - 1);
                                      updateIndividualColor(editIndex, 's', newValue);
                                    }}
                                  ></button>
                                </div>
                              </div>
                              <div className="hsl-slider">
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  style={{ backgroundImage: `linear-gradient(to right, rgb(122, 122, 122) 0%, ${palette[idx]})` }}
                                  value={Math.round(getEditValues(editIndex).s)}
                                  onChange={(e) => {
                                    // Skip processing if we're currently throttled
                                    if (isThrottled.current) return;

                                    // Set throttling flag
                                    isThrottled.current = true;

                                    const newValue = parseInt(e.target.value);
                                    updateIndividualColor(editIndex, 's', newValue);

                                    setTimeout(() => {
                                      isThrottled.current = false;
                                    }, 10);
                                  }}
                                />

                              </div>
                            </div>

                            {/* lightness */}
                            <div>
                              <div className="hsl-text">
                                <label>L</label>
                                <input
                                  type="text"
                                  name="edit-lightness"
                                  value={Math.round(getEditValues(editIndex).l)}
                                  maxLength="3"
                                  pattern="[0-9]*"
                                  onChange={(e) => {
                                    let inputValue = e.target.value.replace(/\D/g, '');
                                    inputValue = inputValue.slice(0, 3);

                                    let numericValue = parseInt(inputValue, 10);
                                    if (isNaN(numericValue) || numericValue < 0 || numericValue > 100) {
                                      numericValue = 0;
                                    }
                                    updateIndividualColor(editIndex, 'l', numericValue);
                                  }}
                                />
                                <span>%</span>
                                <div className="arrow-btns">
                                  <button
                                    onClick={() => {
                                      let newValue = Math.min(100, Math.round(getEditValues(editIndex).l) + 1);
                                      updateIndividualColor(editIndex, 'l', newValue);
                                    }}
                                  ></button>
                                  <button
                                    onClick={() => {
                                      let newValue = Math.max(0, Math.round(getEditValues(editIndex).l) - 1);
                                      updateIndividualColor(editIndex, 'l', newValue);
                                    }}
                                  ></button>
                                </div>
                              </div>
                              <div className="hsl-slider">
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  style={{ backgroundImage: `linear-gradient(to right, rgb(0, 0, 0) 0%, ${palette[idx]}, rgb(255, 255, 255) 100%)` }}
                                  value={Math.round(getEditValues(editIndex).l)}

                                  onChange={(e) => {
                                    // Skip processing if we're currently throttled
                                    if (isThrottled.current) return;

                                    // Set throttling flag
                                    isThrottled.current = true;

                                    const newValue = parseInt(e.target.value);
                                    updateIndividualColor(editIndex, 'l', newValue);

                                    setTimeout(() => {
                                      isThrottled.current = false;
                                    }, 10);
                                  }}
                                />

                              </div>
                            </div>
                          </div>
                          <button
                            className={`reset reset--variable`}
                            disabled={!hasEffectiveChanges(idx)}
                            onClick={(e) => {
                              e.stopPropagation();
                              resetIndividualColor(idx);
                            }}
                          >
                            <span>Reset variable</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}

                <button
                  className={`reset reset--all-variables`}
                  disabled={!hasEditedSquares()}

                  onClick={() => {
                    resetAllIndividualColors();
                  }}
                >
                  <span>Reset all variables</span>
                </button>
              </div>
            }
          </div>
        }
      </div>

    </>
  );
});

export default Mono;